import { supabase } from '../lib/supabase';
import { errorHandler } from '../utils/errorHandler';
import { validationUtils } from '../utils/validation';
import { dataCache } from '../utils/performance';

export const boothService = {
  // Get virtual booth by institute ID
  async getBoothByInstituteId(instituteId) {
    return errorHandler?.handleAsync(async () => {
      // Validate UUID format
      if (!validationUtils?.isValidUUID(instituteId)) {
        console.warn(`Invalid UUID format for institute ID: ${instituteId}`);
        return null;
      }

      // Check cache first
      const cacheKey = `booth_${instituteId}`;
      const cached = dataCache?.get(cacheKey);
      if (cached) return cached;

      const { data, error } = await supabase?.from('virtual_booths')?.select('*')?.eq('institute_id', instituteId)?.single();

      if (error) {
        if (error?.code === 'PGRST116') {
          // No rows returned - not an error, just no booth
          return null;
        }
        throw error;
      }
      
      // Cache the result
      if (data) {
        dataCache?.set(cacheKey, data);
      }
      
      return data;
    }, 'getBoothByInstituteId');
  },

  // Update application links for a booth
  async updateApplicationLinks(instituteId, applicationLinks) {
    return errorHandler?.handleAsync(async () => {
      console.log('[updateApplicationLinks] Starting update:', { instituteId, applicationLinks });
      
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }

      if (!Array.isArray(applicationLinks)) {
        throw new Error('Application links must be an array');
      }

      // Normalize and validate URLs in links
      const normalizedLinks = applicationLinks?.map(link => {
        if (link?.url) {
          // Add https:// if no protocol is present
          let normalizedUrl = link?.url?.trim();
          if (!normalizedUrl?.match(/^https?:\/\//i)) {
            normalizedUrl = `https://${normalizedUrl}`;
          }

          // Validate the normalized URL
          if (!validationUtils?.isValidURL(normalizedUrl)) {
            throw new Error(`Invalid URL: ${link.url}`);
          }

          return { ...link, url: normalizedUrl };
        }
        return link;
      });

      console.log('[updateApplicationLinks] Normalized links:', normalizedLinks);
      console.log('[updateApplicationLinks] Executing Supabase update...');

      const { data, error } = await supabase?.from('virtual_booths')?.update({ application_links: normalizedLinks })?.eq('institute_id', instituteId)?.select()?.single();

      if (error) {
        console.error('[updateApplicationLinks] Supabase error:', error);
        throw error;
      }

      console.log('[updateApplicationLinks] Update successful:', data);

      // Clear cache
      dataCache?.delete(`booth_${instituteId}`);

      return data;
    }, 'updateApplicationLinks');
  },

  // Add a single application link
  async addApplicationLink(instituteId, linkData) {
    return errorHandler?.handleAsync(async () => {
      console.log('[addApplicationLink] Starting add:', { instituteId, linkData });
      
      // First get existing links
      const booth = await this.getBoothByInstituteId(instituteId);
      console.log('[addApplicationLink] Existing booth data:', booth);

      if (!booth) {
        // Create booth record if it doesn't exist
        console.log('[addApplicationLink] No booth found, creating new booth record for institute:', instituteId);
        const { data: newBooth, error: createError } = await supabase
          ?.from('virtual_booths')
          ?.insert({
            institute_id: instituteId,
            application_links: [linkData],
            video_urls: [],
            brochure_urls: [],
            campus_tour_urls: [],
            visit_count: 0
          })
          ?.select()
          ?.single();

        if (createError) {
          console.error('[addApplicationLink] Error creating booth:', createError);
          throw createError;
        }

        // Clear cache
        dataCache?.delete(`booth_${instituteId}`);
        console.log('[addApplicationLink] Booth created successfully with application link:', newBooth);
        return newBooth;
      }

      const existingLinks = booth?.application_links || [];
      console.log('[addApplicationLink] Existing links:', existingLinks);

      // Add new link
      const updatedLinks = [...existingLinks, linkData];
      console.log('[addApplicationLink] Updated links array:', updatedLinks);

      const result = await this.updateApplicationLinks(instituteId, updatedLinks);
      console.log('[addApplicationLink] Final result:', result);
      return result;
    }, 'addApplicationLink');
  },

  // Remove an application link by index
  async removeApplicationLink(instituteId, linkIndex) {
    return errorHandler?.handleAsync(async () => {
      // Get existing links
      const booth = await this.getBoothByInstituteId(instituteId);
      if (!booth) {
        throw new Error('Booth not found');
      }
      
      const existingLinks = booth?.application_links || [];
      
      if (linkIndex < 0 || linkIndex >= existingLinks?.length) {
        throw new Error('Invalid link index');
      }
      
      // Remove link at index
      const updatedLinks = existingLinks?.filter((_, index) => index !== linkIndex);
      
      return await this.updateApplicationLinks(instituteId, updatedLinks);
    }, 'removeApplicationLink');
  },

  // Update video URLs
  async updateVideoUrls(instituteId, videoUrls) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      if (!Array.isArray(videoUrls)) {
        throw new Error('Video URLs must be an array');
      }
      
      const { data, error } = await supabase?.from('virtual_booths')?.update({ video_urls: videoUrls })?.eq('institute_id', instituteId)?.select()?.single();

      if (error) throw error;
      
      // Clear cache
      dataCache?.delete(`booth_${instituteId}`);
      
      return data;
    }, 'updateVideoUrls');
  },

  // Update brochure URLs
  async updateBrochureUrls(instituteId, brochureUrls) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      if (!Array.isArray(brochureUrls)) {
        throw new Error('Brochure URLs must be an array');
      }
      
      const { data, error } = await supabase?.from('virtual_booths')?.update({ brochure_urls: brochureUrls })?.eq('institute_id', instituteId)?.select()?.single();

      if (error) throw error;
      
      // Clear cache
      dataCache?.delete(`booth_${instituteId}`);
      
      return data;
    }, 'updateBrochureUrls');
  },

  // Update campus tour URLs
  async updateCampusTourUrls(instituteId, campusTourUrls) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      if (!Array.isArray(campusTourUrls)) {
        throw new Error('Campus tour URLs must be an array');
      }
      
      const { data, error } = await supabase?.from('virtual_booths')?.update({ campus_tour_urls: campusTourUrls })?.eq('institute_id', instituteId)?.select()?.single();

      if (error) throw error;
      
      // Clear cache
      dataCache?.delete(`booth_${instituteId}`);
      
      return data;
    }, 'updateCampusTourUrls');
  },

  // Add a single campus tour URL
  async addCampusTourUrl(instituteId, tourData) {
    return errorHandler?.handleAsync(async () => {
      // First get existing tours
      const booth = await this.getBoothByInstituteId(instituteId);
      
      if (!booth) {
        // Create booth record if it doesn't exist
        console.log('[addCampusTourUrl] No booth found, creating new booth record for institute:', instituteId);
        const { data: newBooth, error: createError } = await supabase
          ?.from('virtual_booths')
          ?.insert({
            institute_id: instituteId,
            campus_tour_urls: [tourData],
            application_links: [],
            video_urls: [],
            brochure_urls: [],
            visit_count: 0
          })
          ?.select()
          ?.single();
        
        if (createError) {
          console.error('[addCampusTourUrl] Error creating booth:', createError);
          throw createError;
        }
        
        // Clear cache
        dataCache?.delete(`booth_${instituteId}`);
        console.log('[addCampusTourUrl] Booth created successfully with campus tour URL');
        return newBooth;
      }
      
      const existingTours = booth?.campus_tour_urls || [];
      
      // Add new tour
      const updatedTours = [...existingTours, tourData];
      
      return await this.updateCampusTourUrls(instituteId, updatedTours);
    }, 'addCampusTourUrl');
  },

  // Remove a campus tour URL by index
  async removeCampusTourUrl(instituteId, tourIndex) {
    return errorHandler?.handleAsync(async () => {
      // Get existing tours
      const booth = await this.getBoothByInstituteId(instituteId);
      if (!booth) {
        throw new Error('Booth not found');
      }
      
      const existingTours = booth?.campus_tour_urls || [];
      
      if (tourIndex < 0 || tourIndex >= existingTours?.length) {
        throw new Error('Invalid tour index');
      }
      
      // Remove tour at index
      const updatedTours = existingTours?.filter((_, index) => index !== tourIndex);
      
      return await this.updateCampusTourUrls(instituteId, updatedTours);
    }, 'removeCampusTourUrl');
  },

  // Increment visit count
  async incrementVisitCount(instituteId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      const { data, error } = await supabase?.rpc('increment_booth_visit', {
        p_institute_id: instituteId
      });

      if (error) throw error;
      
      // Clear cache
      dataCache?.delete(`booth_${instituteId}`);
      
      return data;
    }, 'incrementVisitCount');
  }
};